import React, { FC, CSSProperties, useState } from "react";
import { ListSubheader, makeStyles, Box } from "@material-ui/core";
import { Project, ProjectType } from "domain-entities";
import ProjectListItem from "./ProjectListItem";
import { Alert } from "@material-ui/lab";
import { useTranslation } from "Providers/TranslationProvider";
import {
	ProjectTree,
	getProjectAddressString,
	getProjectContactsString,
} from "./ProjectHelper";
import FolderIcon from "@material-ui/icons/Folder";
import ArrowBack from "@material-ui/icons/ArrowBack";
import { getColorHex } from "Helpers/ColorHelper";
import { px } from "Configs/theme";
import { VariableSizeList } from "react-window";

interface Props {
	projectTrees: ProjectTree[];
	onClickListItem: (project: Project) => void;
}

const LIST_HEIGHT = 263;
const LIST_SUBHEADER_HEIGHT = 30;
const LIST_ITEM_HEIGHT = 56;
const MAX_LIST_ITEM_HEIGHT = 76;

const ProjectTreeList: FC<Props> = (props) => {
	const { t } = useTranslation();
	const [selectedProjectId, setSelectedProjectId] = useState<string>();
	const selectedProjectTree = getSelectedProject();
	const rows = getRows();
	const classes = useStyles();

	function getSelectedProject() {
		if (selectedProjectId) {
			return props.projectTrees.find(
				(projectTree) => projectTree.project?.id === selectedProjectId
			);
		}
	}

	function getRows() {
		if (selectedProjectTree) {
			return [undefined, ...selectedProjectTree.children];
		} else {
			return props.projectTrees.reduce<Project[]>((result, projectTree) => {
				if (projectTree.project) {
					result.push(projectTree.project);
				} else {
					result.push(...projectTree.children);
				}

				return result;
			}, []);
		}
	}

	function getItemSize(index: number) {
		const row = rows[index];

		if (row) {
			const addressString = getProjectAddressString(row);
			const contactsString = getProjectContactsString(row);

			if (addressString && contactsString) {
				return MAX_LIST_ITEM_HEIGHT;
			} else {
				return LIST_ITEM_HEIGHT;
			}
		} else {
			return LIST_SUBHEADER_HEIGHT;
		}
	}

	function getItemKey(index: number) {
		return rows[index]?.id || "folder";
	}

	function renderRow(rowProps: { index: number; style: CSSProperties }) {
		const row = rows[rowProps.index];

		if (row === undefined && selectedProjectTree?.project) {
			const project = selectedProjectTree?.project;

			return (
				<ListSubheader
					style={rowProps.style}
					className={classes.folderHeader}
					onClick={() => setSelectedProjectId(undefined)}
				>
					<ArrowBack className={classes.folderIcon} />
					<FolderIcon
						className={classes.folderIcon}
						htmlColor={getColorHex(project.colorTag)}
					/>
					{project.name}
				</ListSubheader>
			);
		} else if (row) {
			return (
				<Box style={rowProps.style}>
					<ProjectListItem
						project={row}
						onClick={(project) => {
							if (project.projectType === ProjectType.FOLDER) {
								setSelectedProjectId(project.id);
							} else {
								props.onClickListItem(project);
							}
						}}
					/>
				</Box>
			);
		} else {
			return null;
		}
	}

	if (rows.length) {
		return (
			<VariableSizeList
				key={selectedProjectId || "all"}
				width="100%"
				height={LIST_HEIGHT}
				itemKey={getItemKey}
				itemSize={getItemSize}
				itemCount={rows.length}
				overscanCount={20}
				children={renderRow}
			/>
		);
	} else {
		return (
			<Alert severity="warning">
				{t("ProjectSearchView_Warning_NoProjects")}
			</Alert>
		);
	}
};

export default ProjectTreeList;

const useStyles = makeStyles({
	folderIcon: {
		fontSize: px.xl,
		marginRight: px.m,
	},
	folderHeader: {
		cursor: "pointer",
	},
});
